import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { listExpedientes, deleteExpediente, duplicateExpediente } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, MapTrifold, ArrowRight, Trash, CurrencyDollar, Wallet, ChartLineUp, MagnifyingGlass, Warning, Clock, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

function parseAmount(s) {
  if (!s) return 0;
  const n = parseFloat(String(s).replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// Parse DD/MM/AAAA → Date
function parseDMY(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
}

function daysUntil(date) {
  if (!date) return null;
  const ms = date.getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export default function Dashboard() {
  const { t } = useLang();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [vencFilter, setVencFilter] = useState("all"); // all | proximos | vencidos

  const load = async () => {
    setLoading(true);
    try {
      const data = await listExpedientes();
      setItems(data);
    } catch (e) {
      toast.error(t.error);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(t.confirm_delete)) return;
    try {
      await deleteExpediente(id);
      toast.success("OK");
      load();
    } catch (err) { toast.error(t.error); }
  };

  const onDuplicate = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("¿Duplicar este expediente? Se creará una copia editable. Fotos y timeline se reinician.")) return;
    try {
      const nuevo = await duplicateExpediente(id);
      toast.success("Expediente duplicado");
      navigate(`/expediente/${nuevo.id}`);
    } catch (err) { toast.error(t.error); }
  };

  const progressOf = (exp) => {
    const total = exp.timeline?.length || 9;
    const done = (exp.timeline || []).filter(s => s.completed).length;
    return Math.round((done / total) * 100);  };

  const honorariosSummary = () => {
    let total = 0, cobrado = 0, pendiente = 0;
    items.forEach((e) => {
      const h = e.honorarios || {};
      const m1 = parseAmount(h.monto_1), m2 = parseAmount(h.monto_2);
      total += m1 + m2;
      if (h.monto_1_pagado) cobrado += m1; else pendiente += m1;
      if (h.monto_2_pagado) cobrado += m2; else pendiente += m2;
    });
    return { total, cobrado, pendiente };
  };

  const sum = honorariosSummary();
  const fmt = (n) => n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Búsqueda + filtros
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((e) => {
      // Texto
      if (term) {
        const hay = [
          e.numero_expediente, e.titulo_expediente, e.cliente?.nombre,
          e.titulo?.parcela, e.titulo?.provincia, e.titulo?.municipio,
        ].filter(Boolean).map(String).map((s) => s.toLowerCase()).join(" ");
        if (!hay.includes(term)) return false;
      }
      // Vencimiento
      if (vencFilter !== "all") {
        const venc = parseDMY(e.autorizacion?.fecha_vencimiento);
        const days = daysUntil(venc);
        if (vencFilter === "proximos" && (days === null || days < 0 || days > 7)) return false;
        if (vencFilter === "vencidos" && (days === null || days >= 0)) return false;
      }
      return true;
    });
  }, [items, q, vencFilter]);

  // Stats de vencimiento
  const vencStats = useMemo(() => {
    let proximos = 0, vencidos = 0;
    items.forEach((e) => {
      const venc = parseDMY(e.autorizacion?.fecha_vencimiento);
      const days = daysUntil(venc);
      if (days === null) return;
      if (days < 0) vencidos++;
      else if (days <= 7) proximos++;
    });
    return { proximos, vencidos };
  }, [items]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-6">
        <div>
          <div className="coord-label mb-2">N 18° 28' • W 69° 56' • REP. DOMINICANA</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900">{t.expedientes}</h1>
          <p className="mt-2 text-slate-600">{t.app_tagline}</p>
        </div>
        {can("createExpediente") && (
          <Button
            data-testid="create-new-btn"
            onClick={() => navigate("/nuevo")}
            className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white font-semibold"
          >
            <Plus weight="bold" size={16} className="mr-1.5" />
            {t.nav_new}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm font-mono uppercase tracking-widest">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 bg-white p-12 text-center" data-testid="empty-state">
          <div className="inline-flex w-14 h-14 bg-slate-100 items-center justify-center mb-4">
            <MapTrifold weight="duotone" size={32} color="#f97316" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">{t.no_expedientes}</h3>
          <p className="text-slate-600 mt-1 mb-5">{t.no_expedientes_desc}</p>
          <Button
            data-testid="empty-create-btn"
            onClick={() => navigate("/nuevo")}
            className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus weight="bold" size={16} className="mr-1.5" />
            {t.create_first}
          </Button>
        </div>
      ) : (
        <>
          {/* Honorarios summary — only admin */}
          {can("seeAmounts") && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="honorarios-summary">
            <div className="bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div className="coord-label">{t.hon_total}</div>
                <ChartLineUp weight="duotone" size={20} color="#0f172a" />
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-slate-900" data-testid="hon-total">RD$ {fmt(sum.total)}</div>
            </div>
            <div className="bg-white border border-emerald-200 p-5">
              <div className="flex items-center justify-between">
                <div className="coord-label">{t.hon_cobrado}</div>
                <Wallet weight="duotone" size={20} color="#059669" />
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-emerald-700" data-testid="hon-cobrado">RD$ {fmt(sum.cobrado)}</div>
            </div>
            <div className="bg-white border border-orange-200 p-5">
              <div className="flex items-center justify-between">
                <div className="coord-label">{t.hon_pendiente}</div>
                <CurrencyDollar weight="duotone" size={20} color="#ea580c" />
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-orange-600" data-testid="hon-pendiente">RD$ {fmt(sum.pendiente)}</div>
            </div>
          </div>
          )}

          {/* Alertas de vencimiento */}
          {(vencStats.vencidos > 0 || vencStats.proximos > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="venc-alerts">
              {vencStats.vencidos > 0 && (
                <button
                  data-testid="filter-vencidos"
                  onClick={() => setVencFilter(vencFilter === "vencidos" ? "all" : "vencidos")}
                  className={`bg-white border p-4 text-left transition-colors ${
                    vencFilter === "vencidos" ? "border-red-500 ring-1 ring-red-500" : "border-red-200 hover:border-red-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="coord-label text-red-700">// AUTORIZACIONES VENCIDAS</div>
                    <Warning weight="fill" size={20} className="text-red-500" />
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-mono text-2xl font-bold text-red-600">{vencStats.vencidos}</span>
                    <span className="text-xs text-slate-500">expedientes con oficio vencido</span>
                  </div>
                </button>
              )}
              {vencStats.proximos > 0 && (
                <button
                  data-testid="filter-proximos"
                  onClick={() => setVencFilter(vencFilter === "proximos" ? "all" : "proximos")}
                  className={`bg-white border p-4 text-left transition-colors ${
                    vencFilter === "proximos" ? "border-orange-500 ring-1 ring-orange-500" : "border-orange-200 hover:border-orange-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="coord-label text-orange-700">// PRÓXIMOS A VENCER (≤7 DÍAS)</div>
                    <Clock weight="fill" size={20} className="text-orange-500" />
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-mono text-2xl font-bold text-orange-600">{vencStats.proximos}</span>
                    <span className="text-xs text-slate-500">expedientes para gestionar</span>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Búsqueda + filtros */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Buscar por número, título, cliente, parcela, provincia…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-sm pl-9"
              />
            </div>
            <Select value={vencFilter} onValueChange={setVencFilter}>
              <SelectTrigger data-testid="venc-filter" className="rounded-sm w-full sm:w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los vencimientos</SelectItem>
                <SelectItem value="proximos">Próximos a vencer (≤7 días)</SelectItem>
                <SelectItem value="vencidos">Vencidos</SelectItem>
              </SelectContent>
            </Select>
            {(q || vencFilter !== "all") && (
              <Button
                data-testid="clear-filters"
                variant="outline"
                onClick={() => { setQ(""); setVencFilter("all"); }}
                className="rounded-sm"
              >
                Limpiar
              </Button>
            )}
          </div>

          <div className="border border-slate-200 bg-white overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 coord-label">EXP. NO.</th>
                <th className="px-4 py-3 coord-label">{t.titulo_expediente}</th>
                <th className="px-4 py-3 coord-label">CLIENTE</th>
                <th className="px-4 py-3 coord-label">{t.progress}</th>
                <th className="px-4 py-3 coord-label">VENCIMIENTO</th>
                <th className="px-4 py-3 coord-label">{t.created_at}</th>
                <th className="px-4 py-3 coord-label text-right">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500 font-mono">
                  Sin resultados con los filtros actuales.
                </td></tr>
              )}
              {filtered.map((exp) => {
                const pct = progressOf(exp);
                const venc = parseDMY(exp.autorizacion?.fecha_vencimiento);
                const days = daysUntil(venc);
                let vencCell = <span className="text-slate-400 font-mono text-xs">—</span>;
                if (venc) {
                  if (days < 0) vencCell = <span className="text-xs font-mono px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm">Vencido hace {Math.abs(days)}d</span>;
                  else if (days <= 7) vencCell = <span className="text-xs font-mono px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-sm">{days === 0 ? "Vence hoy" : `Faltan ${days}d`}</span>;
                  else vencCell = <span className="text-xs font-mono text-slate-600">{exp.autorizacion.fecha_vencimiento}</span>;
                }
                return (
                  <tr
                    key={exp.id}
                    data-testid={`exp-row-${exp.id}`}
                    className="border-b border-slate-100 hover:bg-orange-50/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/expediente/${exp.id}`)}
                  >
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-sm font-semibold text-slate-900">{exp.numero_expediente}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-800">{exp.titulo_expediente || "—"}</td>
                    <td className="px-4 py-3.5 text-sm text-slate-700">{exp.cliente?.nombre || "—"}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <div className="flex-1 h-1.5 bg-slate-200 rounded-sm overflow-hidden">
                          <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-xs text-slate-600 w-8 text-right">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5" data-testid={`venc-${exp.id}`}>{vencCell}</td>
                    <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{exp.created_at?.slice(0,10)}</td>
                    <td className="px-4 py-3.5 text-right">
                      {can("createExpediente") && (
                        <Button
                          data-testid={`duplicate-exp-${exp.id}`}
                          size="sm" variant="ghost"
                          onClick={(e) => onDuplicate(exp.id, e)}
                          className="rounded-sm text-slate-400 hover:text-orange-600"
                          title="Duplicar expediente"
                        >
                          <Copy size={14} />
                        </Button>
                      )}
                      {can("deleteExpediente") && (
                        <Button
                          data-testid={`delete-exp-${exp.id}`}
                          size="sm" variant="ghost"
                          onClick={(e) => onDelete(exp.id, e)}
                          className="rounded-sm text-slate-400 hover:text-red-600"
                          title="Eliminar"
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                      <Button
                        data-testid={`open-exp-${exp.id}`}
                        size="sm" variant="ghost"
                        className="rounded-sm text-slate-500 hover:text-orange-600"
                      >
                        <ArrowRight size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
